import { Fragment, type ComponentProps, type ElementType } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  styled,
  SvgIcon,
  Typography,
  type BoxProps,
  type DialogActionsProps,
  type DialogContentProps,
  type StackProps,
  type SvgIconTypeMap,
  type TypographyProps,
} from '@mui/material';
import type { OverridableComponent } from '@mui/material/OverridableComponent';

import { ReactComponent as DeleteIcon } from '@/shared/assets/icons-2.0/delete.svg';
import { ReactComponent as WarningIcon } from '@/shared/assets/icons-2.0/warning.svg';

// ------------------------------------------------------------------

const UploadHeaderIcon = () => <DialogHeaderIcon component={FileUploadOutlinedIcon} color="secondary" />;
const WarningHeaderIcon = () => <DialogHeaderIcon component={WarningIcon} color="warning" />;
const ErrorHeaderIcon = () => <DialogHeaderIcon component={ErrorOutlineOutlinedIcon} color="error" />;
const DoneHeaderIcon = () => <DialogHeaderIcon component={DoneOutlinedIcon} color="secondary" />;
const DeleteHeaderIcon = () => <DialogHeaderIcon component={DeleteIcon} color="error" />;

// ------------------------------------------------------------------

type IconColor = 'secondary' | 'warning' | 'error';

type Variant = 'warning' | 'delete' | 'error' | 'upload' | 'done';

type Icon = ElementType<ComponentProps<'svg'>> | OverridableComponent<SvgIconTypeMap<{}, 'svg'>>;

export type DialogHeaderProps = {
  onClose?: () => void;
  title: React.ReactNode;
  dataTestId?: string;
} & (
  | { variant: Variant; icon?: never; color?: never }
  | { variant?: never; icon: Icon; color: IconColor }
  | { variant?: never; icon?: never; color?: never }
);

const iconsByVariant: Record<Variant, Icon> = {
  warning: WarningHeaderIcon,
  done: DoneHeaderIcon,
  delete: DeleteHeaderIcon,
  error: ErrorHeaderIcon,
  upload: UploadHeaderIcon,
};

// ------------------------------------------------------------------

type HeaderIconProps = {
  variant: DialogHeaderProps['variant'];
  icon: DialogHeaderProps['icon'];
  color: DialogHeaderProps['color'];
};

const HeaderIcon = (props: HeaderIconProps) => {
  const { variant, color, icon } = props;

  if (variant) {
    const IconSVG = iconsByVariant[variant];
    return <IconSVG height={40} width={40} />;
  }

  if (icon && color) {
    return <DialogHeaderIcon component={icon} color={color} />;
  }

  return null;
};

// ------------------------------------------------------------------

export const DialogHeader = (props: DialogHeaderProps) => {
  const { onClose, title, variant, color, icon, dataTestId } = props;

  const isIconRender = variant || (icon && color);

  return (
    <Fragment>
      <Stack
        direction="row"
        sx={{ padding: 1, justifyContent: 'space-between', alignItems: 'center', minHeight: 56 }}
        data-testid={dataTestId}
      >
        <Stack spacing={1} alignItems="center" textAlign="center" direction="row">
          <HeaderIcon variant={variant} icon={icon} color={color} />
          <DialogTitle
            variant="Title/large"
            sx={{ padding: 0, minHeight: 'fit-content', paddingLeft: isIconRender ? 0 : 2 }}
          >
            {title}
          </DialogTitle>
        </Stack>

        <IconButton onClick={onClose}>
          <CloseIcon fontSize="medium" />
        </IconButton>
      </Stack>

      <Divider />
    </Fragment>
  );
};

// ------------------------------------------------------------------

type DialogHeaderIconProps = {
  component: React.ElementType<any>;
  color: 'secondary' | 'warning' | 'error';
};

const StyledIconContainer = styled(Box)({
  borderRadius: '50%',
  aspectRatio: 1,
  width: 40,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

export const DialogHeaderIcon = (props: DialogHeaderIconProps) => {
  const { component, color } = props;
  return (
    <StyledIconContainer sx={{ backgroundColor: `${color}.main` }}>
      <SvgIcon component={component} fontSize="medium" sx={{ color: 'secondary.contrastText' }} />
    </StyledIconContainer>
  );
};

// ------------------------------------------------------------------

export const DialogBodyMutedText = (props: TypographyProps) => (
  <Typography component="div" variant="body2" color="text.secondary" {...props} />
);

export const DialogContentContainer = (props: DialogContentProps) => (
  <DialogContent sx={{ p: theme => theme.spacing(2, 3, 0) }} {...props} />
);

export const DialogActionsContainer = (props: DialogActionsProps) => <DialogActions sx={{ p: 3 }} {...props} />;

// ------------------------------------------------------------------
type DialogCardProps = BoxProps &
  StackProps & {
    title?: string;
  };

const StyledBox = styled(Box)(({ theme }) => ({
  width: '100%',
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.neutral[300]}`,
  backgroundColor: theme.palette.neutral[200],
  padding: theme.spacing(2),
  textAlign: 'left',
  maxHeight: '50vh',
  overflowY: 'auto',

  '&::-webkit-scrollbar': {
    width: '12px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.neutral[500],
    border: '4px solid transparent',
    backgroundClip: 'padding-box',
    borderRadius: '9px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
})) as typeof Box;

export const DialogCard = (props: DialogCardProps) => {
  const { title, children, ...restProps } = props;

  return (
    <Stack spacing={1}>
      {title && (
        <Typography variant="Title/medium" color="neutral.700">
          {title}
        </Typography>
      )}
      <StyledBox component={Stack} divider={<Divider />} spacing={2} {...restProps}>
        {children}
      </StyledBox>
    </Stack>
  );
};
